import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { overlay, content, contentFlush, head, h2, close as closeCls, body, bodyFlush, foot } from './Modal.css';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Accessible name for the dialog. Rendered as the visible header heading
   * (alongside the × close button) unless {@link ModalProps.hideTitle} is set,
   * in which case it is exposed only to assistive technology.
   */
  title?: ReactNode;
  /**
   * Renders {@link ModalProps.title} for screen readers only — no visible header
   * row and no × button. Use for modals whose chrome is owned by their content
   * (the title is still required so the dialog has an accessible name).
   */
  hideTitle?: boolean;
  children: ReactNode;
  /** Footer actions (buttons). */
  footer?: ReactNode;
  /** Accessible label for the × close button. */
  closeLabel?: string;
  /**
   * Overrides the content box's max-width (default `min(27.5rem, 92vw)`).
   * Pass a CSS length, e.g. `"40rem"`.
   */
  maxWidth?: string;
  /**
   * Removes the content's padding and clips it to its rounded corners, so the
   * children can render full-bleed internal chrome (their own pinned header /
   * footer + scrolling body). Pair with `hideTitle`.
   */
  flush?: boolean;
  /**
   * Extra class appended to the content box (composed after the base
   * `content`/`contentFlush` class — does not replace it). The content portals to
   * `document.body`, so a caller whose children reference a scoped CSS-variable
   * theme must pass that theme class here so the portaled subtree defines those
   * vars. (The global design tokens live on `:root`, so they resolve in the
   * portal without this.)
   */
  contentClassName?: string;
}

export function Modal({ open, onClose, title, hideTitle, children, footer, closeLabel = 'Close', maxWidth, flush, contentClassName }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlay} />
        <Dialog.Content className={`${flush ? contentFlush : content}${contentClassName ? ' ' + contentClassName : ''}`} style={maxWidth ? { maxWidth } : undefined}>
          {hideTitle ? (
            <VisuallyHidden asChild>
              <Dialog.Title>{title}</Dialog.Title>
            </VisuallyHidden>
          ) : (
            <div className={head}>
              <Dialog.Title className={h2}>{title}</Dialog.Title>
              <Dialog.Close className={closeCls} aria-label={closeLabel}><span aria-hidden>×</span></Dialog.Close>
            </div>
          )}
          <div className={flush ? bodyFlush : body}>{children}</div>
          {footer && <div className={foot}>{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
